/**
 * TargetPriceInputGroup — the wizard's server-derived input-group bubble
 * (spec §2, Task 11): the `target_price_input_group` metadata the backend
 * stamps on mid-wizard assistant turns (Task 7) rendered on the chat surface.
 *
 * COMPOSITION, not duplication: the rows are the shared ScenarioInputGroup in
 * bubble mode — identity-shared getTpRowStyles singletons, the exact style
 * objects the sheet's rows render, so bubble-sheet row geometry/colors cannot
 * drift. This component owns everything AROUND the rows: the stage title,
 * the free-input commit plumbing, the read-only displacement note, and —
 * since spec §7 single-confirm (task 8) — exactly ONE 「確認」 button
 * (tp-ig-confirm). The per-scenario ✓ chips (tp-ig-accept-<key>) and the
 * 「全部確認」 row are GONE: one stage = one visible act = one POST
 * (fields + accept + edited) on the screen side, completing the stage and
 * triggering the one advance turn. Task 12 mounts the DriverExplainer at the
 * marked slot for the wacc/terminal_growth stages.
 *
 * THE ONE BUTTON: fired with stageKeysFor(group.stage) — the exact key list
 * the screen POSTs as `accept` — and it FLUSHES first. The rows commit on
 * onEndEditing, so a value typed and then confirmed without tapping away used
 * to be dropped outright: the POST carried the AI's suggestion while the input
 * still displayed the user's number (2026-10-28 staging, bear EBIT margin).
 * The button now sweeps the dirty rows through the ordinary commit path and
 * hands the swept values up in the same call, because the screen folds its
 * `fields` from card state that cannot yet show this tick's optimistic commit.
 * It is DISABLED until every row holds a value (spec §7): the
 * accept-on-null guard (final-review I2) moved here from the old per-chip
 * disabling — confirming an absent number is not a thing, so a group with a
 * hole cannot complete its stage at all. Once the stage IS answered — rows
 * stamped, or the confirm POST still in flight — the same button renders
 * spent rather than disappearing, so one stage stays one POST. The `edited`
 * §7 typed subset arrives from the server's fold (`target_price_state.edited`,
 * threaded in by the screen); the button adds
 * only the keys it just flushed, which are typed evidence too — its stamp has
 * not landed in the transcript yet when the confirm POST is built.
 *
 * DISPLACEMENT (spec §2 Global Constraints): `editable` is true ONLY for the
 * group on the LATEST assistant message carrying one (computed by the screen
 * from the message list); every earlier group renders read-only — no inputs,
 * no confirm, just the confirmed values and the localized note — so a
 * displaced group cannot POST.
 *
 * All staged rows are RATE rows (the stage vocabulary is the three
 * probabilities + four percent drivers; base_revenue is BASIS-seeded and has
 * no stage), so the ×100 percent view is unconditional — ScenarioInputGroup
 * divides typed input back to card units before onCommit sees it.
 */
import React, { useMemo, useRef } from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../contexts/ThemeContext';
import { DriverExplainer } from './DriverExplainer';
import type { DriverExplainerWhich } from './DriverExplainer';
import { ScenarioInputGroup } from './ScenarioInputGroup';
import type { ScenarioInputRow, ScenarioInputGroupHandle } from './ScenarioInputGroup';
import { stageKeysFor } from '../../services/targetPriceCard';
import type { TargetPriceCard } from '../../services/targetPriceCard';

/** Mirror of the backend input_group_for_stage payload (Task 7 metadata):
 * `{ stage, rows: [{ key, value, rationale? }] }`. */
export type TargetPriceInputGroupData = {
  stage: string;
  rows: Array<{ key: string; value: number | null; rationale?: string | null }>;
};

/** What the confirm button had to flush to get to its act: a row the user
 * typed into but never blurred. Value in CARD units (decimal, never the
 * percent view); null = cleared. */
export type FlushedRow = { key: string; value: number | null };

// Label tables — the SAME i18n keys the sheet uses (chat.targetPrice.*), so
// the two surfaces cannot disagree on terminology.
const STAGE_LABELS: Record<string, { key: string; fallback: string }> = {
  probabilities: { key: 'chat.targetPrice.probabilities', fallback: 'Probabilities' },
  revenue_growth: {
    key: 'chat.targetPrice.revenueGrowth',
    fallback: 'Revenue growth (CAGR %)',
  },
  ebit_margin: { key: 'chat.targetPrice.ebitMargin', fallback: 'EBIT margin %' },
  wacc: { key: 'chat.targetPrice.wacc', fallback: 'WACC %' },
  terminal_growth: { key: 'chat.targetPrice.terminalGrowth', fallback: 'Terminal growth %' },
};

const SCENARIO_LABELS: Record<string, { key: string; fallback: string }> = {
  bull: { key: 'chat.targetPrice.bull', fallback: 'Bull' },
  base: { key: 'chat.targetPrice.base', fallback: 'Base' },
  bear: { key: 'chat.targetPrice.bear', fallback: 'Bear' },
};

const PROB_LABELS: Record<string, { key: string; fallback: string; scenario: string }> = {
  bull_prob: {
    key: 'chat.targetPrice.bullProb',
    fallback: 'Bull probability %',
    scenario: 'bull',
  },
  base_prob: {
    key: 'chat.targetPrice.baseProb',
    fallback: 'Base probability %',
    scenario: 'base',
  },
  bear_prob: {
    key: 'chat.targetPrice.bearProb',
    fallback: 'Bear probability %',
    scenario: 'bear',
  },
};

const scenarioLabel = (scenario: string, t: (k: string, o?: any) => string): string => {
  const meta = SCENARIO_LABELS[scenario];
  return meta ? t(meta.key, { defaultValue: meta.fallback }) : scenario;
};

export function TargetPriceInputGroup({
  group,
  editable,
  card,
  confirmedKeys,
  confirming,
  onConfirm,
  onCommitRow,
  testIDPrefix = 'tp-ig',
}: {
  group: TargetPriceInputGroupData;
  editable: boolean;
  /** The projected draft card the confirm folds against (the screen handler
   * builds the wire fields from it); a missing card disarms the affordances. */
  card: TargetPriceCard | null;
  /** The §7 confirmation STAMP fold — the server's `target_price_state.confirmed`,
   * which the read-only note is derived from. Displacement alone is not
   * proof of confirmation: a group the user answered in free text («30／50／20，
   * 就照這個») is displaced — the agent wrote those values onto the card — yet
   * nothing was stamped, so the wizard is still asking that stage (2026-10-30
   * staging). An un-stamped displaced group says so instead of claiming ✓.
   * Optional: absent means the server shipped NO fold (review #1), which
   * degrades to "nothing stamped" — the note stays off and the button stays
   * pressable, the loose side, since the server re-checks the stamp anyway. */
  confirmedKeys?: Set<string>;
  /** The screen's confirm mutation is in flight for THIS group. Paired with
   * the stamp fold it gives the button an "answered" state that survives the
   * 20–40 s turn: the stamp lands only when the POST's transcript row is
   * refetched, so without this the button re-arms for the whole turn and every
   * extra tap is another identical `target_price_edit` the agent has to drain
   * (2026-10-31 staging, seq 26/28 — a 132 ms double-tap). */
  confirming?: boolean;
  /** The ONE confirm act (spec §7): fired with stageKeysFor(group.stage) —
   * the exact list the screen POSTs as `accept` — plus the rows the button had
   * to flush first, in CARD units. The screen folds those into the same POST's
   * fields and edited list: its card state cannot show this tick's optimistic
   * commit, and the flush's own provenance stamp has not landed in the
   * transcript the `edited` fold reads. */
  onConfirm?: (keys: string[], flushed: FlushedRow[]) => void;
  /** The free-input commit; value in CARD units (decimal, never the percent
   * view; null = cleared). A typed commit is what feeds the §7 edited set
   * (its own POST stamps edited=[key] when the value changed). */
  onCommitRow?: (key: string, value: number | null) => void;
  testIDPrefix?: string;
}) {
  const { theme } = useTheme();
  const { t } = useTranslation();
  const styles = useMemo(() => createThemedStyles(theme), [theme]);
  const rowsRef = useRef<ScenarioInputGroupHandle | null>(null);

  // The renderer already divided percent drafts back to card units; '' is a
  // cleared row → null value.
  const cardValue = (raw: string): number | null => (raw.trim() === '' ? null : Number(raw));

  // Displacement: an earlier group renders read-only; and even the live
  // stage's affordances stay down until the screen's card state exists (the
  // confirm builds its wire fields from it).
  const armed = editable && card != null;

  const stageMeta = STAGE_LABELS[group.stage];
  const stageLabel = stageMeta
    ? t(stageMeta.key, { defaultValue: stageMeta.fallback })
    : group.stage;

  const rowLabel = (key: string): string => {
    const dot = key.indexOf('.');
    if (dot === -1) {
      const meta = PROB_LABELS[key];
      return meta ? t(meta.key, { defaultValue: meta.fallback }) : key;
    }
    return scenarioLabel(key.slice(0, dot), t);
  };

  const rows: ScenarioInputRow[] = group.rows.map((r) => ({
    key: r.key,
    label: rowLabel(r.key),
    value: r.value,
    rationale: r.rationale ?? null,
    percent: true, // every staged row is a rate — see the file comment
  }));

  // Task 12: the two judgment-heavy rate stages carry the "what is this
  // number?" explainer (collapsed); the other stages have none.
  const explainer: DriverExplainerWhich | null =
    group.stage === 'wacc' || group.stage === 'terminal_growth' ? group.stage : null;

  // Accept-on-null guard, affordance layer (final-review I2, §7 form): the
  // ONE button is disabled until EVERY row holds a value — confirming an
  // absent number is not a thing, so a group with a hole cannot complete its
  // stage. The screen handler re-checks the same rule against the (fresher)
  // card before POSTing.
  const complete = group.rows.every((r) => r.value != null);

  // Whether THESE numbers were actually stamped — every row's key present in
  // the §7 confirmation fold, the same set the compute gate reads. Rows, not
  // stageKeysFor(stage): the note is about the values on screen here. No fold
  // at all (server shipped none) reads as nothing stamped.
  const stamped = group.rows.every((r) => confirmedKeys?.has(r.key) ?? false);

  // ANSWERED, not merely in flight (M4): the stage is done once its rows carry
  // a stamp OR the confirm POST is still travelling. Gating on the stamp alone
  // re-arms the button for the whole turn — the stamp only appears after the
  // refetch, 20–40 s later — and every re-tap is another identical edit the
  // agent must drain. Gating on in-flight alone re-arms it on a network error
  // mid-turn. The label says the wizard is moving on, which is also the "please
  // wait" message the reporter was missing.
  const spent = stamped || confirming === true;

  return (
    <View testID={`${testIDPrefix}-group`} style={styles.group}>
      <Text style={styles.title}>
        {/* Retitled 2026-10-30: 「建議數值 / Suggested values」 was wrong twice
            over — by confirm-time these are the user's own numbers, and the
            builder's whole compliance framing is that the outputs ARE the
            user's scenario values, not ours. */}
        {t('chat.targetPrice.inputGroupTitle', { defaultValue: 'Your assumptions' })} · {stageLabel}
      </Text>
      <ScenarioInputGroup
        ref={rowsRef}
        mode="bubble"
        rows={rows}
        // PR-review fix J: the rows' editable gate is `armed`, not bare
        // `editable` — with no live card there is nothing to commit against,
        // so the inputs disable along with the confirm button (which was
        // already armed-gated).
        editable={armed}
        testIDPrefix={testIDPrefix}
        onCommit={(key, raw) => onCommitRow?.(key, cardValue(raw))}
      />
      {/* Task 12: the explainer mounts here for the wacc / terminal_growth
          stages — below the rows, above the confirm button. It renders for
          displaced (read-only) groups too: the "why" travels with the
          numbers wherever they render. */}
      {explainer && <DriverExplainer which={explainer} />}
      {armed && (
        <Pressable
          testID={`${testIDPrefix}-confirm`}
          style={[styles.confirm, (!complete || spent) && styles.confirmDisabled]}
          disabled={!complete || spent}
          onPress={() =>
            onConfirm?.(
              stageKeysFor(group.stage),
              (rowsRef.current?.flushDirty() ?? []).map((f) => ({
                key: f.key,
                value: cardValue(f.value),
              }))
            )
          }
          accessibilityRole="button"
          accessibilityState={{ disabled: !complete || spent }}
        >
          <Text style={styles.confirmText}>
            {spent
              ? t('chat.targetPrice.confirmedStage', {
                  defaultValue: 'Confirmed — moving on',
                })
              : t('chat.targetPrice.confirmGroup', { defaultValue: '確認' })}
          </Text>
        </Pressable>
      )}
      {!editable && (
        <Text style={styles.readonlyNote}>
          {stamped
            ? t('chat.targetPrice.readOnlyGroup', { defaultValue: 'Confirmed ✓' })
            : t('chat.targetPrice.readOnlyGroupPending', {
                defaultValue: 'Earlier round — not confirmed',
              })}
        </Text>
      )}
    </View>
  );
}

function createThemedStyles(theme: any) {
  return StyleSheet.create({
    group: {
      marginHorizontal: theme.spacing.md, // aligns with the bubble rows (12)
      marginTop: theme.spacing.xxs,
      padding: theme.spacing.md,
      borderRadius: theme.radii.lg,
      backgroundColor: theme.colors.surfaceSecondary,
      gap: theme.spacing.sm,
    },
    title: {
      fontSize: theme.typography.fontSizeSm,
      fontWeight: theme.typography.fontWeightBold,
      color: theme.colors.text,
    },
    confirm: {
      alignItems: 'center',
      paddingVertical: theme.spacing.sm,
      paddingHorizontal: theme.spacing.md,
      borderRadius: theme.radii.md,
      backgroundColor: theme.colors.primary,
    },
    confirmDisabled: {
      opacity: 0.5,
    },
    confirmText: {
      fontSize: theme.typography.fontSizeSm,
      fontWeight: theme.typography.fontWeightSemibold,
      color: theme.colors.textInverse,
    },
    readonlyNote: {
      fontSize: theme.typography.fontSizeXs,
      color: theme.colors.textTertiary,
    },
  });
}
